
import uuid4 from "uuid4";
import Vue, { VNode } from "vue";
import eventBus from "../event-bus";
import { hasElOrSomeParentTheClass } from "../utils/dom";

interface IMouseOrTouchEvent {
	pageX:number;
	pageY:number;
	target:HTMLElement | null;
	preventDefaultFunction:Function | null;
}

export default Vue.extend({
	data() {
		return {
			id: uuid4(),
			ghost: null as HTMLElement | null,
			offset: {
				x: 0,
				y: 0
			},
			selectedIndex: -1,
			eventBus,
			scrollHeight: 0,
			updateScheduleId: 0
		};
	},
	props: {
		items: {
			type: Array as () => Array<any>,
			required: true
		},
		tag: {
			type: String,
			default: "div"
		},
		disableX: Boolean,
		disableY: Boolean,
		keepInY: Boolean,
		targetClass: String,
		indexOffset: {
			type: Number,
			default():number{
				return 0;
			}
		},
		tagAttributes: {
			type: Object as () => any,
			default: () => {}
		},
	},
	computed: {
		dragging():boolean {
			return this.ghost != null;
		}
	},
	watch: {
		"eventBus.mouse": {
			deep: true,
			handler() {
				this.onUpdate();
			}
		},
		dragging(){
			this.eventBus.mouse.dragging = this.dragging;
		}
	},
	methods: {
		// eslint-disable-next-line max-lines-per-function
		onUpdate() {
			if (!this.dragging) return;
			if (!this.ghost) return;
			if (!eventBus.mouse.left) {
				this.ghost.remove();
				this.ghost = null;
				this.selectedIndex = -1;
				this.$emit("drop");
				return;
			}
			if (!this.disableX) {
				this.ghost.style.left = (eventBus.mouse.x - this.offset.x) + "px";
			}
			if (!this.disableY) {
				this.ghost.style.top = (eventBus.mouse.y - this.offset.y) + "px";
			}
			if (this.keepInY) {
				let top = parseInt(this.ghost.style.top || "0px");
				if (this.scrollHeight < top) {
					this.ghost.style.top = this.scrollHeight + "px";
				}
				if (top < 0) {
					this.ghost.style.top = "0px";
				}
			}
			if (this.selectedIndex == -1) return;

			let hoveredIndex = -1;
			let defaultSlotItems = this.getDefaultSlotItems();
			let top = parseInt(this.ghost.style.top || "0px") + 3;
			let left = parseInt(this.ghost.style.left || "0px") + 3;

			for (let i in defaultSlotItems) {
				let elm = defaultSlotItems[i].elm;
				if (!elm) continue;
				if (elm == this.ghost) continue;
				let html = elm as HTMLElement;
				if (!this.disableY) {
					if (html.offsetTop < top) {
						hoveredIndex = parseInt(i);
					}
				}else if (!this.disableX) {
					if (html.offsetLeft < left) {
						hoveredIndex = parseInt(i);
					}
				}

			}

			if (hoveredIndex == -1) return;
			hoveredIndex+=this.indexOffset;
			if (hoveredIndex < 0){
				hoveredIndex = 0;
			}
			if (hoveredIndex >= this.items.length){
				hoveredIndex = this.items.length-1;
			}
			this.items.splice(hoveredIndex, 0, this.items.splice(this.selectedIndex, 1)[0]);
			this.selectedIndex = hoveredIndex;
		},
		getDefaultSlotItems():VNode[] {
			if (!this.$slots.default) {
				return [];
			}
			return this.$slots.default.filter((node) => {
				if (!node.tag) {
					return true;
				}
				if (node.tag.indexOf("w-scroll-view-thumb") != -1){
					return false;
				}
				if (node.elm && (node.elm as HTMLElement).classList && (node.elm as HTMLElement).classList.contains("sortable-ignore")){
					return false;
				}
				return true;
			});
		},
		onScroll(v:Vue, diff:{diffX:number, diffY:number}){
			if (!this.dragging) return;
			let parent:Element | null = this.$el;
			let found = false;
			while (parent != null){
				if (parent == v.$el){
					found = true;
					break;
				}
				parent = parent.parentElement;
			}
			if (!found) {
				return;
			}
			if (!this.disableY) {
				this.offset.y -= diff.diffY;
			}
			if (!this.disableX) {
				this.offset.x -= diff.diffX;
			}
			this.scheduleUpdate();
		},
		scheduleUpdate(){
			if (this.updateScheduleId != 0) return;
			this.updateScheduleId = setTimeout(()=>{
				this.onUpdate();
				this.updateScheduleId = 0;
			}, 30);
		},
		onMouseDown(event:IMouseOrTouchEvent){
			let ref = document.getElementById(this.id) as HTMLElement;
			if (!ref) return;
			let target = event.target as HTMLElement;
			if (!target) return;
			if (this.targetClass) {
				if (!hasElOrSomeParentTheClass(target, this.targetClass, ref)) {
					return;
				}
			}
			if (event.preventDefaultFunction){
				event.preventDefaultFunction();
			}
			this.scrollHeight = ref.scrollHeight;
			this.selectedIndex = -1;
			setTimeout(this.onMouseDownTimeout(target, ref, event), 100);
		},
		onMouseDownTimeout(target:HTMLElement, ref:HTMLElement, event:IMouseOrTouchEvent):Function{
			return ()=>{
				let el = target as HTMLElement | null;
				while (el != null) {
					let parent = el.parentElement;
					if (parent == ref) {
						target = el;
						break;
					}
					el = parent;
				}
				let defaultSlotItems = this.getDefaultSlotItems();
				for (let i = 0; i < defaultSlotItems.length; i++) {
					if (defaultSlotItems[i].elm == target) {
						this.selectedIndex = i+this.indexOffset;
					}
				}
				if (this.selectedIndex == -1) {
					return;
				}
				this.ghost = target.cloneNode(true) as HTMLElement;
				this.ghost.style.width = window.getComputedStyle(target).getPropertyValue("width");
				this.ghost.classList.add("w-sortable-ghost");
				this.offset.x = event.pageX - target.offsetLeft;
				this.offset.y = event.pageY - target.offsetTop;
				ref.appendChild(this.ghost);
				this.onUpdate();
			};
		}
	},
	mounted() {
		let ref = document.getElementById(this.id) as HTMLElement;
		if (!ref) throw new Error("ELEMENT DOES NOT EXIST");
		eventBus.$on("scroll", this.onScroll);
		ref.style.position = "relative";
		ref.classList.add("w-sortable");
		ref.addEventListener("mousedown", (event:MouseEvent)=>{
			let newEvent = {
				pageX: event.pageX,
				pageY: event.pageY,
				target: event.target
			} as IMouseOrTouchEvent;
			this.onMouseDown(newEvent);
		});
		ref.addEventListener("touchstart", (event:TouchEvent)=>{
			let newEvent = {
				pageX: (event.touches.item(0) as Touch).pageX,
				pageY: (event.touches.item(0) as Touch).pageY,
				target: event.target,
				preventDefaultFunction: ()=>{
					event.preventDefault();
				}
			} as IMouseOrTouchEvent;
			this.onMouseDown(newEvent);
		});
	},
	destroyed(){
		this.eventBus.$off("scroll", this.onScroll);
	},
	render(createElement):VNode {
		let node = createElement(this.tag, {attrs: {id: this.id, ...this.tagAttributes}}, this.$slots.default);
		return node;
	}
});
